import React, { useEffect, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { useGetChainParityCompetitors } from '~/hooks/apis/price-parity/useGetChainParityCompetitors';
import { useGetChainParityList } from '~/hooks/apis/price-parity/useGetChainParityList';
import { useGetChainParityProperties } from '~/hooks/apis/price-parity/useGetChainParityProperties';
import { useGetChainParityRangeSummary } from '~/hooks/apis/price-parity/useGetChainParityRangeSummary';
import { useGetChainParitySeries } from '~/hooks/apis/price-parity/useGetChainParitySeries';

import type { ChainWeightingMode, ParityChainListItem } from '~/types/services/priceParrot';

import { PARITY_SECTION_BLOCK_MB, SECTION_SX } from '../PriceParrotParityTool/constants';
import {
  buildCalendarDatesInclusive,
  getParityTrendFullWindowStart,
} from '../PriceParrotParityTool/parityTrendDateRange';
import { fmtShortDateWithYear } from '../PriceParrotParityTool/utils';
import TogglePillGroup from '../shared/TogglePillGroup';

import CompetitorChart from './CompetitorChart';
import DailyTrendChart from './DailyTrendChart';
import PropertiesTable from './PropertiesTable';
import SummaryTiles from './SummaryTiles';

const PROPERTIES_LIMIT_DEFAULT = 15;

const PERIOD_PRESETS: ReadonlyArray<{ key: string; label: string; days: number }> = [
  { key: '7d', label: '7d', days: 7 },
  { key: '30d', label: '30d', days: 30 },
  { key: '3mo', label: '3mo', days: 90 },
  { key: '6mo', label: '6mo', days: 180 },
  { key: '12mo', label: '12mo', days: 365 },
];

function errMsg(err: unknown): string {
  if (!err) {
    return '';
  }
  return err instanceof Error ? err.message : String(err);
}

function presetKeyFor(indices: [number, number], calendarLength: number): string | null {
  const maxIdx = calendarLength - 1;
  if (maxIdx < 0 || indices[1] !== maxIdx) {
    return null;
  }
  const span = indices[1] - indices[0] + 1;
  return PERIOD_PRESETS.find((p) => p.days === span)?.key ?? null;
}

export default function PriceParrotChainParityDashboard() {
  // Snapshot "today" and the FY-aligned full window at mount. The trend / competitor section
  // date pickers can drill anywhere in the FY window; the rollup + properties default to a
  // strict 365-day window so the 12mo preset pill matches the default range exactly.
  const { calendarDates, initialRange } = useMemo(() => {
    const end = dayjs().startOf('day');
    const start = getParityTrendFullWindowStart(end);
    const dates = buildCalendarDatesInclusive(start, end);
    const len = dates.length;
    const range: [number, number] = len > 1 ? [Math.max(0, len - 365), len - 1] : [0, 0];
    return { calendarDates: dates, initialRange: range };
  }, []);

  const [committedIndices, setCommittedIndices] = useState<[number, number]>(initialRange);
  // Properties table has its own preset range so changing the table's Period pill doesn't
  // disturb the rollup tiles / chain list. Defaults to the same window as the rollup.
  const [propertiesCommittedIndices, setPropertiesCommittedIndices] = useState<[number, number]>(initialRange);

  const fullDateFrom = calendarDates[0] ?? '';
  const fullDateTo = calendarDates[calendarDates.length - 1] ?? '';

  // Fixed last-12mo window used as the default + reset target for the trend / competitor
  // sections. Computed from `initialRange` (not from the live `committedIndices`) so the
  // Reset button always restores the original 12mo span, regardless of any rollup changes.
  const last12moFrom = calendarDates[initialRange[0]] ?? '';
  const last12moTo = calendarDates[initialRange[1]] ?? '';

  const dateFrom = calendarDates[committedIndices[0]] ?? '';
  const dateTo = calendarDates[committedIndices[1]] ?? '';

  const propertiesDateFrom = calendarDates[propertiesCommittedIndices[0]] ?? '';
  const propertiesDateTo = calendarDates[propertiesCommittedIndices[1]] ?? '';

  const [selectedChain, setSelectedChain] = useState<string | null>(null);
  const [weighting, setWeighting] = useState<ChainWeightingMode>('impressions');
  const [continent, setContinent] = useState<string | null>(null);

  // Trend / competitor sections have their own independent ranges so users can drill into a
  // sub-window without disturbing the rollup. State is declared up front; the floor-aware
  // reset closures are defined below `effectiveMinDate` so they can clamp to the chain floor.
  const [trendDateFrom, setTrendDateFrom] = useState<string>(last12moFrom);
  const [trendDateTo, setTrendDateTo] = useState<string>(last12moTo);
  const [competitorDateFrom, setCompetitorDateFrom] = useState<string>(last12moFrom);
  const [competitorDateTo, setCompetitorDateTo] = useState<string>(last12moTo);
  const [selectedCompetitor, setSelectedCompetitor] = useState<string | null>(null);

  // Chain list is keyed off the committed range so the dropdown reflects what's
  // queryable for the current selection. Otherwise we'd offer chains with no data.
  const {
    data: chainList,
    isLoading: isChainsLoading,
    isError: isChainsError,
    error: chainsError,
  } = useGetChainParityList(dateFrom, dateTo);

  // Auto-pick the top chain by total_jobs when none is selected, or when the
  // range change drops the current chain out of the list (otherwise the
  // Autocomplete looks empty while queries fire against a chain with no data).
  useEffect(() => {
    const chains = chainList?.chains;
    if (!chains?.length) {
      return;
    }
    const stillPresent = selectedChain ? chains.some((c) => c.chain_name === selectedChain) : false;
    if (selectedChain && stillPresent) {
      return;
    }
    const sorted = [...chains].sort((a, b) => b.total_jobs - a.total_jobs);
    setSelectedChain(sorted[0].chain_name);
  }, [chainList?.chains, selectedChain]);

  const {
    data: summaryData,
    isFetching: isSummaryFetching,
    isError: isSummaryError,
    error: summaryError,
  } = useGetChainParityRangeSummary(selectedChain ?? undefined, dateFrom, dateTo, weighting, continent);

  // Separate unfiltered summary fetch used only to populate the continent option list, so the
  // pills stay stable when a continent is selected (otherwise the filtered summary would return
  // only that continent and the other pills would disappear, making the toggle look broken).
  const { data: continentOptionsSummaryData } = useGetChainParityRangeSummary(
    selectedChain ?? undefined,
    dateFrom,
    dateTo,
    weighting,
    null,
  );

  const {
    data: seriesData,
    isFetching: isSeriesFetching,
    isError: isSeriesError,
    error: seriesError,
  } = useGetChainParitySeries(selectedChain ?? undefined, trendDateFrom, trendDateTo, weighting, continent);

  // Trend-section summary — drives the per-continent parity score tile next to the chart.
  // Independent of the rollup summary so the trend can scope to a different window.
  const { data: trendSummaryData, isFetching: isTrendSummaryFetching } = useGetChainParityRangeSummary(
    selectedChain ?? undefined,
    trendDateFrom,
    trendDateTo,
    weighting,
    continent,
  );

  const {
    data: propertiesData,
    isFetching: isPropertiesFetching,
    isError: isPropertiesError,
    error: propertiesError,
  } = useGetChainParityProperties(
    selectedChain ?? undefined,
    propertiesDateFrom,
    propertiesDateTo,
    PROPERTIES_LIMIT_DEFAULT,
    continent,
    weighting,
  );

  const {
    data: competitorsData,
    isFetching: isCompetitorsFetching,
    isError: isCompetitorsError,
    error: competitorsError,
  } = useGetChainParityCompetitors(selectedChain ?? undefined, competitorDateFrom, competitorDateTo, continent);

  // Competitor-section summary — drives the range-aggregated KPI tile and the pill list.
  // svc-vendor puts `major_competitors` + `avg_premium_by_competitor` on the summary, so we
  // re-query summary scoped to this section's range (independent of the trend section's range).
  const { data: competitorSummaryData, isFetching: isCompetitorSummaryFetching } = useGetChainParityRangeSummary(
    selectedChain ?? undefined,
    competitorDateFrom,
    competitorDateTo,
    weighting,
    continent,
  );

  // Chain-specific historical floor. The chain dashboard rolls up Price Parrot scrapes; for
  // chains that started being scraped recently, picking dates earlier than this floor produces
  // empty/no-data ranges. We clamp all calendar controls (presets + pickers) to this floor so
  // users can't accidentally select past it. Null until the summary fetch lands.
  // Source the floor from the *unfiltered* summary so it stays stable when a continent is
  // picked. BE docs say `data_available_from` is chain-global, but reading from the filter-
  // independent fetch is the resilient choice — also dedupes via TanStack when no continent
  // is selected.
  const dataFloor = continentOptionsSummaryData?.summary?.data_available_from ?? null;
  const effectiveMinDate = useMemo(() => {
    if (!dataFloor) {
      return fullDateFrom;
    }
    return dataFloor > fullDateFrom ? dataFloor : fullDateFrom;
  }, [dataFloor, fullDateFrom]);

  const commitPreset = (days: number, setter: (next: [number, number]) => void) => {
    const maxIdx = calendarDates.length - 1;
    if (maxIdx < 0) {
      return;
    }
    // Keep the literal span at `days` (don't clamp to the data floor) so the preset pill
    // stays highlighted. Pre-floor days just come back as zeros from svc-vendor — harmless,
    // and aligns with how the trend / competitor sections handle the same case.
    const fromIdx = Math.max(0, maxIdx - (days - 1));
    setter([fromIdx, maxIdx]);
  };

  const handlePeriodPreset = (days: number) => commitPreset(days, setCommittedIndices);
  const handlePropertiesPeriodPreset = (days: number) => commitPreset(days, setPropertiesCommittedIndices);

  // Reset targets respect the chain's data floor — resetting on a chain whose floor is later
  // than `last12moFrom` should snap to the floor, not into an empty pre-data void.
  const resetFrom = dataFloor && dataFloor > last12moFrom ? dataFloor : last12moFrom;
  const resetTrendRange = () => {
    setTrendDateFrom(resetFrom);
    setTrendDateTo(last12moTo);
  };
  const resetCompetitorRange = () => {
    setCompetitorDateFrom(resetFrom);
    setCompetitorDateTo(last12moTo);
  };

  // When a new chain's data floor lands (or moves forward), clamp the trend / competitor
  // section pickers if their current `from` predates the floor. The rollup / properties
  // preset ranges are intentionally NOT clamped — keeping them at the literal preset span
  // means the period pill stays highlighted, and pre-floor days come back as zeros from
  // svc-vendor (harmless).
  useEffect(() => {
    if (!dataFloor) {
      return;
    }
    if (trendDateFrom && trendDateFrom < dataFloor) {
      setTrendDateFrom(dataFloor);
    }
    if (competitorDateFrom && competitorDateFrom < dataFloor) {
      setCompetitorDateFrom(dataFloor);
    }
    // trendDateFrom / competitorDateFrom intentionally omitted — we only want to clamp on
    // floor changes, not on user-driven range edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFloor]);

  const activePresetKey = useMemo(
    () => presetKeyFor(committedIndices, calendarDates.length),
    [committedIndices, calendarDates.length],
  );

  const propertiesActivePresetKey = useMemo(
    () => presetKeyFor(propertiesCommittedIndices, calendarDates.length),
    [propertiesCommittedIndices, calendarDates.length],
  );

  const chainOptions = useMemo<Array<ParityChainListItem>>(
    () => (chainList?.chains ?? []).slice().sort((a, b) => a.chain_name.localeCompare(b.chain_name)),
    [chainList?.chains],
  );

  // Continent pill list is driven by an unfiltered summary so the option set stays stable when
  // the user picks a continent. If we sourced it from the filtered `summaryData`, selecting
  // "Europe" would collapse the list to just ["Europe"] (the filtered response only carries
  // continents present in the filtered result), making the toggle look broken.
  const continentOptions = useMemo<Array<{ key: string; label: string; value: string | null }>>(() => {
    const continents = continentOptionsSummaryData?.summary?.continents ?? [];
    return [
      { key: 'all', label: 'All', value: null },
      ...continents.map((c) => ({ key: c.toLowerCase(), label: c, value: c })),
    ];
  }, [continentOptionsSummaryData?.summary?.continents]);

  // Reset continent on chain change so we don't briefly fire a filtered query against a chain
  // that may not have that continent.
  useEffect(() => {
    setContinent(null);
    setSelectedCompetitor(null);
  }, [selectedChain]);

  // Auto-pick the highest-volume major-competitor whenever the available set changes.
  // Source of truth is BE: `summary.major_competitors` is the curated bucket list,
  // `summary.avg_premium_by_competitor` carries the weights for ranking.
  const majorCompetitors = useMemo<Array<string>>(
    () => competitorSummaryData?.summary?.major_competitors ?? [],
    [competitorSummaryData?.summary?.major_competitors],
  );

  useEffect(() => {
    if (!majorCompetitors.length) {
      if (selectedCompetitor) {
        setSelectedCompetitor(null);
      }
      return;
    }
    if (selectedCompetitor && majorCompetitors.includes(selectedCompetitor)) {
      return;
    }
    const aggregates = competitorSummaryData?.summary?.avg_premium_by_competitor ?? [];
    const ranked = [...majorCompetitors].sort((a, b) => {
      const aw = aggregates.find((x) => x.major_competitor === a)?.comparisons_le_overpriced ?? 0;
      const bw = aggregates.find((x) => x.major_competitor === b)?.comparisons_le_overpriced ?? 0;
      return bw - aw || a.localeCompare(b);
    });
    setSelectedCompetitor(ranked[0]);
  }, [majorCompetitors, competitorSummaryData?.summary?.avg_premium_by_competitor, selectedCompetitor]);

  // If the active continent disappears from the option list (e.g. range narrowed past any rows
  // for that continent), drop the filter rather than leaving the user stuck.
  useEffect(() => {
    if (continent == null) {
      return;
    }
    const present = continentOptions.some((opt) => opt.value === continent);
    if (!present) {
      setContinent(null);
    }
  }, [continent, continentOptions]);

  // Connected segmented control (e.g. Impressions | Margin), black selected
  const modePillSx = {
    bgcolor: 'background.paper',
    borderRadius: 2,
    '& .MuiToggleButtonGroup-grouped': {
      border: 0,
      px: 2,
      py: 0.75,
      fontWeight: 600,
      textTransform: 'none',
      color: 'text.primary',
      '&:hover': { bgcolor: 'background.paper' },
      '&.Mui-selected, &.Mui-selected:hover': {
        bgcolor: 'grey.900',
        color: 'common.white',
      },
    },
  } as const;

  const rollupTitle = selectedChain ? `${selectedChain} — Chain parity rollup` : 'Chain parity rollup';

  return (
    <Box>
      {/* Top bar: chain picker */}
      <Box sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Autocomplete<ParityChainListItem>
            options={chainOptions}
            getOptionLabel={(opt) => opt.chain_name}
            value={chainOptions.find((c) => c.chain_name === selectedChain) ?? null}
            onChange={(_, value) => setSelectedChain(value?.chain_name ?? null)}
            isOptionEqualToValue={(a, b) => a.chain_name === b.chain_name}
            loading={isChainsLoading}
            sx={{ minWidth: 320, flex: 1, maxWidth: 560 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Hotel chain"
                size="small"
                placeholder="Search chains…"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isChainsLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.chain_name}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                  <span>{option.chain_name}</span>
                  <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {option.property_count} properties · {option.total_jobs.toLocaleString()} scrapes
                  </Typography>
                </Box>
              </li>
            )}
          />
        </Stack>

        {isChainsError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to load chains: {errMsg(chainsError)}
          </Alert>
        )}
      </Box>

      {/* Scope disclaimer — flags that the rollup is bounded by what Price Parrot has actually
        scraped, and surfaces the historical floor (`data_available_from`) so users picking a
        range that predates the chain's earliest scrape don't mistake a thin view for chain-wide
        truth. */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 13 }}>
          This rollup only reflects chain properties that Price Parrot has scraped within the selected range
          {summaryData?.summary?.property_count != null ? (
            <>
              {' '}
              (<strong>{summaryData.summary.property_count}</strong> in this view)
            </>
          ) : null}
          , so figures below may <strong>not</strong> be exhaustive for the chain as a whole.
          {summaryData?.summary?.data_available_from ? (
            <>
              {' '}
              Scrapes for this chain go back to{' '}
              <strong>{fmtShortDateWithYear(summaryData.summary.data_available_from)}</strong> — earlier dates have no
              data.
            </>
          ) : null}{' '}
          Use the per-property Parity Tool to scrape properties that haven&apos;t been covered yet.
        </Typography>
      </Alert>

      {/* Rollup card */}
      <Paper elevation={0} sx={SECTION_SX}>
        <Box sx={{ mb: PARITY_SECTION_BLOCK_MB }}>
          <Typography sx={{ fontSize: 24, fontWeight: 600 }}>{rollupTitle}</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5, maxWidth: 720 }}>
            Aggregated parity across all member properties in this chain. Score is weighted by{' '}
            <strong>impressions</strong> or <strong>margin</strong>. Reporting range applies to the rollup, properties,
            and competitor metrics — the trend chart below uses the full history window.
          </Typography>
        </Box>

        {/* Controls: weighted score + period preset on one row */}
        <Box sx={{ bgcolor: 'grey.100', borderRadius: 2, px: 2, py: 1.5, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 4 }}
            alignItems={{ md: 'center' }}
            useFlexGap
            flexWrap="wrap"
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mr: 1 }}>
                Weighted score
              </Typography>
              <ToggleButtonGroup
                value={weighting}
                exclusive
                size="small"
                onChange={(_, v) => v && setWeighting(v as ChainWeightingMode)}
                aria-label="weighted score"
                sx={modePillSx}
              >
                <ToggleButton value="impressions">Impressions</ToggleButton>
                <ToggleButton value="margin">Margin</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <TogglePillGroup
              label="Period"
              ariaLabel="Quick range presets"
              options={PERIOD_PRESETS.map((preset) => ({ value: preset.key, label: preset.label }))}
              value={activePresetKey ?? ''}
              onChange={(key) => {
                const preset = PERIOD_PRESETS.find((p) => p.key === key);
                if (preset) {
                  handlePeriodPreset(preset.days);
                }
              }}
            />
          </Stack>
        </Box>

        <SummaryTiles
          summary={summaryData?.summary ?? null}
          rangeLabel={
            dateFrom && dateTo ? `${fmtShortDateWithYear(dateFrom)} – ${fmtShortDateWithYear(dateTo)}` : undefined
          }
          isLoading={isSummaryFetching}
        />
        {isSummaryError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to load summary: {errMsg(summaryError)}
          </Alert>
        )}
      </Paper>

      <DailyTrendChart
        daily={seriesData?.daily_trend ?? []}
        isLoading={isSeriesFetching}
        isError={isSeriesError}
        errorMessage={errMsg(seriesError)}
        continent={continent}
        continentOptions={continentOptions}
        onContinentChange={setContinent}
        dateFrom={trendDateFrom}
        dateTo={trendDateTo}
        minDate={effectiveMinDate}
        maxDate={fullDateTo}
        onDateFromChange={setTrendDateFrom}
        onDateToChange={setTrendDateTo}
        onResetRange={resetTrendRange}
        summary={trendSummaryData?.summary ?? null}
        summaryLoading={isTrendSummaryFetching}
      />

      <CompetitorChart
        chainName={selectedChain}
        daily={competitorsData?.daily_trend ?? []}
        majorCompetitors={majorCompetitors}
        aggregates={competitorSummaryData?.summary?.avg_premium_by_competitor ?? []}
        selectedCompetitor={selectedCompetitor}
        onCompetitorChange={setSelectedCompetitor}
        weighting={weighting}
        dateFrom={competitorDateFrom}
        dateTo={competitorDateTo}
        minDate={effectiveMinDate}
        maxDate={fullDateTo}
        onDateFromChange={setCompetitorDateFrom}
        onDateToChange={setCompetitorDateTo}
        onResetRange={resetCompetitorRange}
        isLoading={isCompetitorsFetching || isCompetitorSummaryFetching}
        isError={isCompetitorsError}
        errorMessage={errMsg(competitorsError)}
      />

      <PropertiesTable
        rows={propertiesData?.properties ?? []}
        isLoading={isPropertiesFetching}
        isError={isPropertiesError}
        errorMessage={errMsg(propertiesError)}
        periodPresets={PERIOD_PRESETS}
        activePresetKey={propertiesActivePresetKey}
        onPeriodPresetChange={handlePropertiesPeriodPreset}
        weighting={weighting}
        totalPropertyCount={propertiesData?.total_property_count}
      />
    </Box>
  );
}
